import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-compat";
import { MEDIA_TARGETS } from "@/lib/media-buckets";
import { teamFullName, type TeamMember } from "@/lib/team.queries";
import {
  createTeamMember,
  deleteTeamMember,
  listTeamMembersAdmin,
  updateTeamMember,
} from "@/lib/team.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Image as ImageIcon, User } from "lucide-react";
import { MediaField } from "@/components/admin/MediaField";
import { Field } from "@/components/admin/Field";
import { FormDialog, FormSection, SaveButton, useDirty } from "@/components/admin/FormDialog";

export const Route = createFileRoute("/_authenticated/admin/team")({
  component: TeamAdminPage,
});

type FormState = {
  id?: string;
  title: string;
  first_name: string;
  last_name: string;
  suffix: string;
  position: string;
  short_bio: string;
  long_bio: string;
  photo_path: string;
  email: string;
  linkedin_url: string;
  is_active: boolean;
};

const empty: FormState = {
  title: "",
  first_name: "",
  last_name: "",
  suffix: "",
  position: "",
  short_bio: "",
  long_bio: "",
  photo_path: "",
  email: "",
  linkedin_url: "",
  is_active: true,
};

function TeamAdminPage() {
  const listAdmin = useServerFn(listTeamMembersAdmin);
  const { data: members = [] } = useQuery({
    queryKey: ["team_members", "admin"],
    queryFn: async () => (await listAdmin()) as TeamMember[],
  });
  const qc = useQueryClient();
  const create = useServerFn(createTeamMember);
  const update = useServerFn(updateTeamMember);
  const del = useServerFn(deleteTeamMember);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(empty);
  /**
   * Rows saved before migration 023 hold a bare filename in photo_path; the
   * server's photo_url is the only thing that resolves those, so it is kept
   * for the preview until the field is repointed at a full /media URL.
   */
  const [legacyPhotoUrl, setLegacyPhotoUrl] = useState<string | null>(null);
  // True while the MediaField's Upload/Library picker (sibling modal) is up.
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [q, setQ] = useState("");
  const dirty = useDirty(form, open);

  const sorted = useMemo(
    () =>
      [...members].sort((a, b) =>
        a.last_name.localeCompare(b.last_name) || a.first_name.localeCompare(b.first_name),
      ),
    [members],
  );
  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return sorted;
    return sorted.filter(
      (m) =>
        teamFullName(m).toLowerCase().includes(n) ||
        (m.position ?? "").toLowerCase().includes(n),
    );
  }, [sorted, q]);

  function reset() {
    setForm(empty);
    setLegacyPhotoUrl(null);
  }

  const previewSrc = /^(\/|https?:\/\/)/.test(form.photo_path)
    ? form.photo_path
    : form.photo_path
      ? legacyPhotoUrl
      : null;

  function loadEdit(m: TeamMember) {
    setForm({
      id: m.id,
      title: m.title ?? "",
      first_name: m.first_name,
      last_name: m.last_name,
      suffix: m.suffix ?? "",
      position: m.position ?? "",
      short_bio: m.short_bio ?? "",
      long_bio: m.long_bio ?? "",
      photo_path: m.photo_path ?? "",
      email: m.email ?? "",
      linkedin_url: m.linkedin_url ?? "",
      is_active: m.is_active,
    });
    setLegacyPhotoUrl(m.photo_url);
    setOpen(true);
  }

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title || null,
        first_name: form.first_name,
        last_name: form.last_name,
        suffix: form.suffix || null,
        position: form.position || null,
        short_bio: form.short_bio || null,
        long_bio: form.long_bio || null,
        photo_path: form.photo_path || null,
        email: form.email || null,
        linkedin_url: form.linkedin_url || null,
        is_active: form.is_active,
      };
      if (form.id) await update({ data: { id: form.id, ...payload } });
      else await create({ data: payload });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team_members"] });
      toast.success("Saved");
      setOpen(false);
      reset();
    },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => del({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team_members"] });
      toast.success("Deleted");
    },
    onError: (e: any) => toast.error(e.message ?? "Delete failed"),
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Our Team</CardTitle>
            <CardDescription>
              Leadership team displayed on About Us via the <code>[LIST:OurTeam]</code> template variable.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-56"
            />
            <Button
              onClick={() => {
                reset();
                setOpen(true);
              }}
            >
              <Plus className="mr-1 h-4 w-4" /> New
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-card shadow-sm">
                <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="p-3">Photo</th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Position</th>
                  <th className="p-3">Active</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m.id} className="border-b hover:bg-secondary/30">
                    <td className="p-3">
                      <div className="grid h-10 w-10 place-items-center overflow-hidden rounded-full bg-secondary">
                        {m.photo_url ? (
                          <img src={m.photo_url} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <User className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </td>
                    <td className="p-3 font-medium">{teamFullName(m)}</td>
                    <td className="p-3 text-muted-foreground">{m.position}</td>
                    <td className="p-3">{m.is_active ? "Yes" : "No"}</td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="icon" onClick={() => loadEdit(m)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (confirm(`Delete ${teamFullName(m)}?`)) remove.mutate(m.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-6 text-center text-muted-foreground">
                      No team members.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <FormDialog
        open={open}
        onOpenChange={setOpen}
        size="md"
        icon={User}
        title={form.id ? "Edit team member" : "New team member"}
        context={form.id ? teamFullName(form) : "New leadership-team entry"}
        dirty={dirty}
        busy={libraryOpen || save.isPending}
        asForm
        onSubmit={() => {
          if (form.first_name && form.last_name) save.mutate();
        }}
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <SaveButton
              dirty={dirty}
              pending={save.isPending}
              disabled={!form.first_name || !form.last_name}
            />
          </>
        }
      >
        <div className="space-y-4">
          <FormSection title="Photo" cols={1}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <div className="grid h-28 w-28 shrink-0 place-items-center overflow-hidden rounded-lg border bg-secondary">
                {previewSrc ? (
                  <img src={previewSrc} alt="" className="h-full w-full object-cover" />
                ) : (
                  <ImageIcon className="h-8 w-8 text-muted-foreground" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                {/* photo_path stores the full /media URL (or https URL). */}
                <MediaField
                  label="Photo"
                  value={form.photo_path}
                  target={MEDIA_TARGETS.teamPhoto}
                  preview="none"
                  onChange={(url) => setForm((f) => ({ ...f, photo_path: url }))}
                  onPickerOpenChange={setLibraryOpen}
                />
              </div>
            </div>
          </FormSection>

          <FormSection title="Identity">
            <Field label="Title">
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Mr./Ms./Dr." />
            </Field>
            <Field label="Suffix">
              <Input value={form.suffix} onChange={(e) => setForm({ ...form, suffix: e.target.value })} placeholder="Jr., MD" />
            </Field>
            <Field label="First name" required>
              <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            </Field>
            <Field label="Last name" required>
              <Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </Field>
            <Field label="Position" full>
              <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
            </Field>
            <Field label="Email">
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="LinkedIn URL">
              <Input value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} />
            </Field>
          </FormSection>

          <FormSection title="Bio" cols={1}>
            <Field label="Short bio (one or two sentences)">
              <Textarea rows={2} value={form.short_bio} onChange={(e) => setForm({ ...form, short_bio: e.target.value })} />
            </Field>
            <Field label="Long bio (shown in dialog)">
              <Textarea rows={8} value={form.long_bio} onChange={(e) => setForm({ ...form, long_bio: e.target.value })} />
            </Field>
            <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
              <Switch id="team-active" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <Label htmlFor="team-active" className="cursor-pointer">{form.is_active ? "Active" : "Inactive"}</Label>
            </div>
          </FormSection>
        </div>
      </FormDialog>
    </div>
  );
}
